// import { utils } from '@intrasync/utils';
import LoadingIcon from '@mui/icons-material/HourglassEmpty';
import { Button } from '@mui/material';
import { action, makeObservable, observable, reaction } from 'mobx';
import React from 'react';
import { Tooltip } from '../Tooltip';
import injectStores from '../RootStore/injectStores';

interface FileUploadButtonProps {
  children?: React.ReactNode;
  disabled?: boolean;
  justIcon?: boolean;
  size?: 'small' | 'medium' | 'large';
  color?:
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning';
  round?: boolean;
  fullWidth?: boolean;
  simple?: boolean;
  block?: boolean;
  link?: boolean;
  loading?: boolean;
  showFileCount?: boolean;
  multiple?: boolean;
  tooltip?: string;
  tooltipPlacement?: 'bottom' | 'top' | 'left' | 'right';
  autosize?: boolean;
  autoClearTimeout?: number;
  handleFiles?: (files: FileList) => void;
  viewStore?: { isMobile: boolean };
}

class FileUploadButton extends React.PureComponent<FileUploadButtonProps> {
  fileCount = 0;
  fileNames: string[] = [];

  fileDispose;
  timeoutMessageDispose: string | number | NodeJS.Timeout | undefined;

  constructor(props: FileUploadButtonProps) {
    super(props);
    makeObservable(this, {
      fileCount: observable,
      fileNames: observable,
      setUploadFiles: action,
      resetFiles: action,
    });

    if (props.autoClearTimeout && props.autoClearTimeout > 0) {
      this.fileDispose = reaction(
        () => this.hasImages,
        (hasFiles) => {
          if (hasFiles) {
            this.timeoutMessageDispose = setTimeout(() => {
              this.resetFiles();
            }, props.autoClearTimeout);
          }
        }
      );
    }
  }

  componentWillUnmount() {
    this.resetFiles();
    this.fileDispose && this.fileDispose();
    this.timeoutMessageDispose && clearTimeout(this.timeoutMessageDispose);
  }

  resetFiles = () => {
    this.fileCount = 0;
    this.fileNames = [];
  };

  setUploadFiles = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { handleFiles } = this.props;
    if (event.target.files) {
      this.fileCount = event.target.files.length;
      this.fileNames = [...event.target.files].map((item) => item.name);
      if (handleFiles) {
        handleFiles(event.target.files);
      }
    }
  };

  get hasImages() {
    return this.fileCount > 0;
  }

  render() {
    const {
      children,
      disabled,
      justIcon,
      size,
      color,
      round,
      fullWidth,
      simple,
      block,
      link,
      loading,
      showFileCount,
      multiple,
      tooltip,
      tooltipPlacement,
      autosize,
      viewStore,
    } = this.props;

    let buttonDisabled = disabled;
    let renderer = children;
    let buttonColor = color;
    let buttonSize = size;

    if (autosize) {
      buttonSize = viewStore?.isMobile ? 'small' : undefined;
    }

    if (loading) {
      buttonDisabled = true;
      renderer = justIcon ? <LoadingIcon /> : 'Loading…';
    }

    if (buttonDisabled) {
      buttonColor = 'inherit';
    }

    const RenderButton = (
      <Button
        variant="contained"
        component="label"
        disabled={buttonDisabled}
        color={buttonColor}
        size={buttonSize}
        fullWidth={fullWidth}
        style={{
          borderRadius: round ? '50%' : undefined,
          display: block ? 'block' : undefined,
          textDecoration: link ? 'underline' : undefined,
          padding: simple ? '6px 12px' : undefined,
        }}
      >
        {renderer}
        {(showFileCount || multiple) && !justIcon && ` (${this.fileCount})`}
        <input
          onChange={(event) => {
            if (!buttonDisabled) this.setUploadFiles(event);
          }}
          hidden
          multiple={multiple}
          type="file"
        />
      </Button>
    );

    if (tooltip || justIcon) {
      let toolTipRender = tooltip || 'Upload';
      if (this.fileCount > 0 && this.fileNames.length > 0) {
        toolTipRender =
          this.fileCount > 1
            ? `${tooltip}\nFiles:\n${this.fileNames.join('\n')}`
            : `${tooltip}\nFile: ${this.fileNames[0]}`;
      }
      return (
        <Tooltip title={toolTipRender} placement={tooltipPlacement}>
          <span style={{ margin: 0, padding: 0 }}>{RenderButton}</span>
        </Tooltip>
      );
    }

    return RenderButton;
  }
}

export default injectStores(FileUploadButton);
